import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { isOnlinePosting, isPortalWithoutArchiveOption } from '@panel/commons/helpers';
import Finish from './Actions/Finish';
import Archive from './Actions/Archive';
import Publish from './Actions/Publish';
import UpdateResponsible from './Actions/UpdateResponsible';
import MoreActionsButton from './Actions/MoreActionsButton';

const ActionMenu = ({
  postings,
  setBulkActionsModals,
  bulkActionsModals,
  device,
  userCanEditResponsible,
  checkAddedPostings
}) => {
  const [validActions, setValidActions] = useState([]);
  const offlineStates = ['DRAFT', 'OFFLINE', 'DELETED'];
  const unAuthorizedStates = ['DRAFT'];
  const forbiddenStates = ['DELETED'];
  const isPostingWithoutArchiveOp =
    isPortalWithoutArchiveOption() && postings.some(p => p.checked && isOnlinePosting(p.stateAndDates[0].status));
  // Se filtran los postings que han sido seleccionados (checked === true):
  const checkedPostings = postings && postings.filter(item => item.checked);

  // PUBLISH VALIDATION:
  const publishAvailable =
    postings &&
    postings.some(p => p.checked) &&
    !postings.some(p => p.checked && unAuthorizedStates.some(state => state === p.stateAndDates[0].status)) &&
    !(
      postings.some(p => p.checked && p.publicationType !== 'Clasificado') &&
      postings.some(p => p.checked && p.publicationType === 'Clasificado')
    );

  // FINISH VALIDATION:
  const finishAvailable =
    postings &&
    postings.some(p => p.checked) &&
    !postings.some(p => p.checked && offlineStates.some(state => state === p.stateAndDates[0].status));

  // ARCHIVE VALIDATION:
  const archiveAvailable =
    postings &&
    postings.some(p => p.checked) &&
    !postings.some(p => p.checked && forbiddenStates.some(state => state === p.stateAndDates[0].status)) &&
    !isPostingWithoutArchiveOp;

  // RESPONSIBLE VALIDATION:
  // Se valida tipo de usuario y publicador, que almenos se haya seleccionado un posting y en caso de ser más de uno, tengan el mismo publisherId (misma sucursal):
  const updateResponsibleAvailable =
    userCanEditResponsible &&
    checkedPostings.length > 0 &&
    checkedPostings.every(item => item.publisherId === checkedPostings[0].publisherId);

  useEffect(() => {
    const enabledActions = [];

    // Se valida que cada acción esté habilitada para mostrarse:
    if (publishAvailable) {
      enabledActions.push({
        id: 'publish',
        component: (
          <Publish
            device={device}
            bulkActionsModals={bulkActionsModals}
            setBulkActionsModals={setBulkActionsModals}
            postings={postings}
          />
        )
      });
    }
    if (finishAvailable) {
      enabledActions.push({
        id: 'finish',
        component: (
          <Finish
            device={device}
            bulkActionsModals={bulkActionsModals}
            setBulkActionsModals={setBulkActionsModals}
            postings={postings}
          />
        )
      });
    }
    if (archiveAvailable) {
      enabledActions.push({
        id: 'archive',
        component: (
          <Archive
            device={device}
            bulkActionsModals={bulkActionsModals}
            setBulkActionsModals={setBulkActionsModals}
            postings={postings}
          />
        )
      });
    }
    if (updateResponsibleAvailable) {
      enabledActions.push({
        id: 'updateResponsible',
        component: (
          <UpdateResponsible
            device={device}
            bulkActionsModals={bulkActionsModals}
            setBulkActionsModals={setBulkActionsModals}
            postings={postings}
            userCanEditResponsible={userCanEditResponsible}
          />
        )
      });
    }
    setValidActions(enabledActions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkAddedPostings]);

  /**
   *La función valida la cantidad de acciones disponibles para determinar si mostrarlas dentro del botón de más acciones o no:
   * @returns Devuelve un componente de React.
   */
  const getTabletActions = () => {
    const firstAction = validActions[0]?.component;
    const moreActions = validActions.slice(1);

    // Si se tiene sólo 1 ó 2 acciones se mostrarán fuera del botón de más acciones:
    if (validActions.length <= 2) {
      return (
        <Container>
          {validActions.map(({ id, component }) => (
            <React.Fragment key={id}>{component}</React.Fragment>
          ))}
        </Container>
      );
    }
    // En caso de tener 3 ó más acciones se mostrará 1 acción y el resto dentro del botón de más acciones:
    return (
      <Container>
        {firstAction}
        {moreActions.length > 0 && checkAddedPostings.length > 0 && (
          <MoreActionsButton
            actions={moreActions}
            device={device}
            bulkActionsModals={bulkActionsModals}
            setBulkActionsModals={setBulkActionsModals}
            postings={postings}
            userCanEditResponsible={userCanEditResponsible}
          />
        )}
      </Container>
    );
  };

  return (
    <Container>
      {device === 'desktop' ? (
        <>
          <Finish
            device={device}
            bulkActionsModals={bulkActionsModals}
            setBulkActionsModals={setBulkActionsModals}
            postings={postings}
          />
          <Archive
            device={device}
            bulkActionsModals={bulkActionsModals}
            setBulkActionsModals={setBulkActionsModals}
            postings={postings}
          />
          <Publish
            device={device}
            bulkActionsModals={bulkActionsModals}
            setBulkActionsModals={setBulkActionsModals}
            postings={postings}
          />
          <UpdateResponsible
            device={device}
            bulkActionsModals={bulkActionsModals}
            setBulkActionsModals={setBulkActionsModals}
            postings={postings}
            userCanEditResponsible={userCanEditResponsible}
          />
        </>
      ) : (
        getTabletActions()
      )}
    </Container>
  );
};

export default ActionMenu;

const Container = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;
